/**
 * What the work of a spoken turn shows (spec 2026-09-12 §6), framework-free.
 * The relayed RouterEvents fold through the SSE lane's own reducer so a spoken
 * ask and a typed ask can never disagree about the truth; from the fold, the
 * line's snapshot and the stage's check flag come the receipt for the crown,
 * the trouble line, the credits door and the one glyph in the glass. Only
 * the receipt and trouble are ever words; the face carries the rest.
 */
import type { ComposerPanel, RouterEvent } from "@bash/shared";
import {
  reduceEvent,
  type ConverseUiState,
} from "@/components/composer/useConverse";
import { isHttpsUrl } from "@/components/composer/routerActions";
import { DRAWN_KINDS } from "@/components/composer/panels/drawn";
import type { LineSnapshot, LineStep } from "./lineMachine";

export type WorkGlyph = "search" | "read" | "create" | "check" | "nami";

export interface TaskReceipt {
  kind: "task_created" | "task_followup";
  taskId: string;
  title: string;
  /** The one sentence the model wrote for this request; null on an older action. */
  say: string | null;
  model: string | null;
  /** The person's pick when the task runs on something else: a substitution
   *  is never silent, so the crown says the swap as the inline card does. */
  substitutedFrom: string | null;
}

/** The Line moved you (spec 2026-09-17 §5.4): the crown shows the place the way it shows a born task. */
export interface AppReceipt {
  kind: "app_control";
  label: string;
  route: string;
}

/**
 * The Line handed back a link (R-L11): the crown holds it as a tappable row,
 * so "the link is on your screen" is true whether or not the automatic open
 * (gesture-less, and often blocked) went through. https only, the belt's own
 * gate, applied where the receipt is folded: a link the belt would refuse is
 * no receipt at all, never a row with nothing behind it.
 */
export interface LinkReceipt {
  kind: "open_url";
  label: string;
  url: string;
}

export type WorkReceipt = TaskReceipt | AppReceipt | LinkReceipt;

/** What a receipt is keyed on across renders: the task, the place, or the link. */
export const receiptKey = (r: WorkReceipt): string =>
  r.kind === "app_control" ? r.route : r.kind === "open_url" ? r.url : r.taskId;

/**
 * The receipt would announce the task the person is already inside (the
 * founder, 2026-09-21: "when inside task sending a message in task showing
 * that on top of bash is not expected"). A follow-up spoken on the task's own
 * page lands as their bubble in that column, and the bubble IS the receipt;
 * the paper would be a second, louder copy of it over the bottom of the story.
 * Only a task's own receipt can say this: a place and a link are elsewhere by
 * definition.
 */
const announcesTheScreen = (
  r: WorkReceipt | null,
  viewingTaskId: string | null
): boolean =>
  r !== null &&
  (r.kind === "task_created" || r.kind === "task_followup") &&
  viewingTaskId !== null &&
  r.taskId === viewingTaskId;

export interface WorkFold {
  ui: ConverseUiState;
  receipt: WorkReceipt | null;
  /** A newer ask took this turn's place: it shows nothing. */
  superseded: boolean;
}

export interface WorkView {
  receipt: WorkReceipt | null;
  /** The line on the paper: under the receipt, or alone. */
  trouble: string | null;
  /** The wallet emptied: the paper carries the Top up door. */
  credits: boolean;
  glyph: WorkGlyph | null;
  /** The crown is up: something is on the paper. */
  up: boolean;
  /** The crown's one panel (composer panels, spec 2026-09-19 §3.4); null when none is up. */
  panel: ComposerPanel | null;
}

export const TROUBLE_AFTER = "Something after this did not go through.";
export const TROUBLE_ALONE = "That did not go through.";
export const CREDITS_LINE = "Out of credits.";

/** The mark each step wears. The two steps the face carries alone are
 *  excluded by name rather than left out, so a seventh step is a type error
 *  here until someone says which mark it wears, or that it wears none. */
const STEP_GLYPH: Record<Exclude<LineStep, "thinking" | "other">, WorkGlyph> = {
  search: "search",
  read: "read",
  create: "create",
  nami: "nami",
};

const NOTHING: WorkView = {
  receipt: null,
  trouble: null,
  credits: false,
  glyph: null,
  up: false,
  panel: null,
};

function receiptOf(ui: ConverseUiState): WorkReceipt | null {
  if (ui.phase !== "routed") return null;
  const a = ui.action;
  if (a.kind === "app_control")
    return { kind: "app_control", label: a.label, route: a.route };
  if (a.kind === "open_url")
    return isHttpsUrl(a.url)
      ? { kind: "open_url", label: a.label, url: a.url }
      : null;
  if (a.kind !== "task_created" && a.kind !== "task_followup") return null;
  const say = typeof a.say === "string" && a.say.trim() ? a.say.trim() : null;
  const model = a.kind === "task_created" && a.model ? a.model : null;
  const substitutedFrom =
    a.kind === "task_created" && a.substitutedFrom ? a.substitutedFrom : null;
  return {
    kind: a.kind,
    taskId: a.taskId,
    title: a.title,
    say,
    model,
    substitutedFrom,
  };
}

export function foldTurn(turn: RouterEvent[]): WorkFold {
  let ui: ConverseUiState = { phase: "thinking" };
  let receipt: WorkReceipt | null = null;
  let superseded = false;
  for (const event of turn) {
    ui = reduceEvent(ui, event);
    receipt = receiptOf(ui) ?? receipt;
    if (event.type === "done" && event.reason === "superseded")
      superseded = true;
  }
  return { ui, receipt, superseded };
}

export function deriveWork({
  fold,
  held,
  line,
  check,
  panel = null,
  viewingTaskId = null,
}: {
  fold: WorkFold;
  held: WorkReceipt | null;
  line: LineSnapshot;
  check: boolean;
  panel?: ComposerPanel | null;
  /** The task whose page is on screen, when one is (screen-context's rule:
   *  a task route with that task picked); null anywhere else. */
  viewingTaskId?: string | null;
}): WorkView {
  // The crown draws a sheet, a receipt or a connect card (drawn.ts); a kind no
  // host draws yet (confirm) lifts nothing.
  const panelUp = panel !== null && DRAWN_KINDS.has(panel.kind);
  // A newer ask took this turn's place: nothing of the turn on the paper and the
  // glyph goes, even if the machine's step has not caught up yet. The panel is
  // not the turn's: it stays until it is closed.
  if (fold.superseded) return { ...NOTHING, panel, up: panelUp };
  const failed = fold.ui.phase === "error";
  const credits = fold.ui.phase === "credits";
  // The held receipt is for trouble that lands after the receipt sank (the
  // same delegation's late failure): never on its own, or the dismiss that
  // emptied the turn would bring the paper straight back.
  const made = fold.receipt ?? (failed || credits ? held : null);
  const trouble = credits
    ? CREDITS_LINE
    : failed
      ? made
        ? TROUBLE_AFTER
        : TROUBLE_ALONE
      : null;
  // Quiet inside the task on screen: the receipt raises no paper, so there is
  // no linger and nothing for the stage to call a clean sink. Trouble is not a
  // receipt though: a follow-up that failed is never silent, and its line says
  // "after this", so the paper comes up with its task under the words.
  const receipt =
    trouble === null && announcesTheScreen(made, viewingTaskId) ? null : made;
  const up = receipt !== null || trouble !== null || panelUp;
  // Every mark in the glass needs an open line: the machine clears the step
  // and the task on close too, but a closing ball shows no work either way,
  // and the check is the stage's own flag, which nothing on the line resets:
  // a receipt sinking after the line closed must never lift a ball at rest.
  const openLine = line.phase === "open";
  let glyph: WorkGlyph | null = null;
  if (
    openLine &&
    line.step !== null &&
    line.step !== "thinking" &&
    line.step !== "other"
  )
    glyph = STEP_GLYPH[line.step];
  else if (check && openLine) glyph = "check";
  // A running task holds no mark: tasks are many and long, and their card
  // carries their state; the glass shows a turn's own moments only (the
  // founder, 2026-09-13: "for running we need not show an icon").
  return { receipt, trouble, credits, glyph, up, panel };
}
